import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import RecruiterDashboard from './RecruiterDashboard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Building2, UserSearch, User, Briefcase, TrendingUp } from 'lucide-react';
import ResponsiveNavigation from '../components/ResponsiveNavigation';

const Dashboard = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  // Redirect based on role
  useEffect(() => {
    if (userProfile?.role === 'jobasti_admin') {
      navigate('/admin/dashboard', { replace: true });
    } else if (userProfile?.role === 'sales_executive') {
      navigate('/sales-dashboard', { replace: true });
    } else if (userProfile?.role === 'prospect') {
      navigate('/prospect-dashboard', { replace: true });
    }
  }, [userProfile, navigate]);

  // Show recruiter dashboard for recruiters
  if (userProfile && ['client_admin', 'client_recruiter'].includes(userProfile.role)) {
    return <RecruiterDashboard />;
  }

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'jobasti_admin':
        return 'Platform Administrator';
      case 'client_admin':
        return 'Administrator';
      case 'client_recruiter':
        return 'Recruiter';
      case 'prospect':
        return 'Prospect';
      default:
        return 'User';
    }
  };

  const getWelcomeMessage = (role) => {
    switch (role) {
      case 'jobasti_admin':
        return 'Manage the entire recruitment platform, users, and system settings.';
      case 'client_admin':
        return 'Oversee your company\'s recruitment activities and manage your team.';
      case 'client_recruiter':
        return 'Find and manage prospects for your recruitment needs.';
      case 'prospect':
        return 'Explore opportunities and manage your professional profile.';
      default:
        return 'Welcome to JobastiIQ recruitment platform.';
    }
  };

  const getQuickActions = (role) => {
    switch (role) {
      case 'jobasti_admin':
        return [
          { title: 'Manage Users', description: 'View and manage all platform users', icon: Users, href: '/users' },
          { title: 'Company Management', description: 'Oversee all companies on the platform', icon: Building2, href: '/clients' },
          { title: 'View Prospects', description: 'Browse all candidate profiles', icon: UserSearch, href: '/prospects' },
          { title: 'System Analytics', description: 'Platform usage and performance metrics', icon: TrendingUp, href: '/analytics' },
        ];
      case 'client_admin':
      case 'client_recruiter':
        return [
          { title: 'Browse Prospects', description: 'Find potential candidates', icon: UserSearch, href: '/sourcing' },
          { title: 'Company Profile', description: 'Manage your company information', icon: Building2, href: '/clients' },
          { title: 'Active Searches', description: 'Track ongoing recruitment searches', icon: Briefcase, href: '/searches' },
          { title: 'Team Management', description: 'Manage your recruitment team', icon: Users, href: '/team' },
        ];
      case 'prospect':
        return [
          { title: 'My Profile', description: 'Update your professional profile', icon: User, href: '/profile' },
          { title: 'Job Opportunities', description: 'View available positions', icon: Briefcase, href: '/opportunities' },
          { title: 'Applications', description: 'Track your job applications', icon: TrendingUp, href: '/applications' },
          { title: 'Skills Assessment', description: 'Complete skill assessments', icon: Users, href: '/assessments' },
        ];
      default:
        return [];
    }
  };

  const quickActions = getQuickActions(userProfile?.role);

  return (
    <div className="min-h-screen bg-gray-50">
      <ResponsiveNavigation />
      
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Welcome back, {userProfile?.firstName || userProfile?.email?.split('@')[0]}!
            </h1>
            <p className="mt-2 text-base lg:text-lg text-gray-600">
              {getRoleDisplayName(userProfile?.role)}
            </p>
            <p className="mt-1 text-sm lg:text-base text-gray-500">
              {getWelcomeMessage(userProfile?.role)}
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Active Prospects
                </CardTitle>
                <UserSearch className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-xl lg:text-2xl font-bold">1,234</div>
                <p className="text-xs text-muted-foreground">
                  +12% from last month
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Companies
                </CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-xl lg:text-2xl font-bold">89</div>
                <p className="text-xs text-muted-foreground">
                  +5 new this month
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Active Searches
                </CardTitle>
                <Briefcase className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-xl lg:text-2xl font-bold">34</div>
                <p className="text-xs text-muted-foreground">
                  8 filled this week
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Success Rate
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-xl lg:text-2xl font-bold">78%</div>
                <p className="text-xs text-muted-foreground">
                  +3% improvement
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 lg:mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2 text-base lg:text-lg">
                        <Icon className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600" />
                        <span className="truncate">{action.title}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm">
                        {action.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 lg:mb-6">Recent Activity</h2>
            <Card>
              <CardHeader>
                <CardTitle>Latest Updates</CardTitle>
                <CardDescription>
                  Recent activity on your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium">New prospect registered</p>
                      <p className="text-xs text-gray-500">2 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium">Position filled successfully</p>
                      <p className="text-xs text-gray-500">5 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-2 h-2 bg-yellow-600 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium">Company profile updated</p>
                      <p className="text-xs text-gray-500">1 day ago</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium">New search criteria saved</p>
                      <p className="text-xs text-gray-500">2 days ago</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;